
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { searchStocks } from "@/utils/api";
import { Skeleton } from "@/components/ui/skeleton";
import { debounce } from "lodash";

const popularStocks = [
  { symbol: "AAPL", name: "Apple Inc." },
  { symbol: "MSFT", name: "Microsoft Corporation" },
  { symbol: "GOOGL", name: "Alphabet Inc." },
  { symbol: "AMZN", name: "Amazon.com Inc." },
  { symbol: "META", name: "Meta Platforms Inc." },
  { symbol: "TSLA", name: "Tesla Inc." },
  { symbol: "NVDA", name: "NVIDIA Corporation" },
  { symbol: "JPM", name: "JPMorgan Chase & Co." },
  { symbol: "V", name: "Visa Inc." },
  { symbol: "WMT", name: "Walmart Inc." },
  { symbol: "JNJ", name: "Johnson & Johnson" },
  { symbol: "MA", name: "Mastercard Inc." },
  { symbol: "NFLX", name: "Netflix Inc." },
  { symbol: "WBD", name: "Warner Bros. Discovery Inc." },
  { symbol: "SHEL", name: "Shell PLC" },
  { symbol: "XOM", name: "Exxon Mobil Corporation" },
  { symbol: "PARA", name: "Paramount Global" },
  { symbol: "DIS", name: "The Walt Disney Company" },
  { symbol: "BP", name: "BP p.l.c." },
  { symbol: "SONY", name: "Sony Group Corporation" },
  { symbol: "CVX", name: "Chevron Corporation" },
  { symbol: "TTE", name: "TotalEnergies SE" },
  { symbol: "CMCSA", name: "Comcast Corporation" },
  { symbol: "T", name: "AT&T Inc." }
];

const Index = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");

  const { data: searchResults, isLoading } = useQuery({
    queryKey: ['stockSearch', searchTerm],
    queryFn: () => searchStocks(searchTerm),
    enabled: searchTerm.length >= 2,
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
  });

  const displayStocks = searchTerm.length >= 2 
    ? (searchResults?.data || []).filter((stock: any) => stock.country === 'United States')
    : popularStocks;

  return (
    <div className="p-8 bg-gradient-to-b from-background to-muted/20">
      <div className="max-w-7xl mx-auto space-y-8">
        <div className="text-center space-y-6">
          <h1 className="text-4xl font-bold">Investment Return Calculator</h1>
          <p className="text-xl text-muted-foreground">
            Calculate Your Historical Stock Investment Returns
          </p>
          <div className="max-w-3xl mx-auto text-muted-foreground space-y-4">
            <p>
              Want to know how much money you could have made by investing in stocks years ago? 
              Our free investment calculator helps you analyze historical stock performance and calculate 
              potential returns, including dividends and stock splits.
            </p>
            <p>
              Explore returns from popular stocks like Apple (AAPL), Tesla (TSLA), Microsoft (MSFT), 
              and many more. Perfect for analyzing different investment scenarios and understanding 
              the power of long-term investing.
            </p>
          </div>
        </div>
        
        <div className="max-w-md mx-auto">
          <Input
            type="text"
            placeholder="Search any US stock symbol or company name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full"
            aria-label="Stock search"
          />
        </div>

        <div className="space-y-8">
          <h2 className="text-2xl font-semibold text-center">Popular Stock Investment Calculators</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {isLoading ? (
              Array(6).fill(0).map((_, i) => (
                <Card key={i} className="p-6">
                  <div className="space-y-4">
                    <Skeleton className="h-8 w-24" />
                    <Skeleton className="h-6 w-48" />
                    <Skeleton className="h-4 w-32 mt-4" />
                  </div>
                </Card>
              ))
            ) : (
              displayStocks.map((stock: any) => (
                <Card
                  key={stock.symbol}
                  className="p-6 hover:shadow-lg transition-all cursor-pointer hover:scale-105"
                  onClick={() => navigate(`/stock/${stock.symbol}`)}
                >
                  <div className="space-y-4">
                    <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                      {stock.symbol}
                    </div>
                    <div className="text-lg text-muted-foreground">
                      {stock.name || stock.instrument_name}
                    </div>
                    <div className="text-sm text-muted-foreground mt-4">
                      Calculate {stock.symbol} investment returns →
                    </div>
                  </div>
                </Card>
              ))
            )}
          </div>
          
          <div className="max-w-3xl mx-auto text-muted-foreground space-y-4 text-center">
            <h2 className="text-2xl font-semibold">Features of Our Investment Calculator</h2>
            <ul className="text-left list-disc pl-6 space-y-2">
              <li>Calculate potential returns from past investments in any US stock</li>
              <li>Analyze different investment periods and amounts</li>
              <li>Include dividend reinvestment in calculations</li>
              <li>Account for stock splits automatically</li>
              <li>Compare returns across different companies</li>
              <li>Visualize investment growth with interactive charts</li>
            </ul>
            <p className="mt-6">
              Start analyzing popular stocks like Tesla, Apple, or Microsoft to understand 
              historical returns and make more informed investment decisions.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Index;
